import dayjs, { type Dayjs } from 'dayjs';
import { TimesheetSubmissionFrequency } from '@core/coreLoopConfiguration/coreLoopConfiguration.types';
import {
  BreakType,
  type DaySequenceChangeInput,
} from '@core/generated/gql-types';
import type { TimesheetApprovalItem } from '@core/timesheetApproval/timesheetApproval.types';
import { minutesToMilliseconds } from '@core/util/datetime.util';
import {
  getCurrentPayrunCycleDate,
  getLastPayrunCycleDate,
} from '@core/util/periodTimesheetBlock.util';
import { getTimestampFromTime } from '@/utils/utils';

/** One candidate's timesheets for a single client, shown as a single row. */
export interface TimesheetGroup {
  /** `${clientId}:${userId}` so the same candidate can appear once per client. */
  rowKey: string;
  clientId: string;
  clientName: string;
  userId: string;
  workerName: string;
  workerProfileImageUrl?: string | null;
  items: TimesheetApprovalItem[];
  startDateISO: string;
  endDateISO: string;
}

export const timesheetRowKey = (clientId: string, userId: string): string =>
  `${clientId}:${userId}`;

export interface TimesheetFilters {
  search: string;
  statuses: string[];
  clientNames: string[];
  accountManagers: string[];
  siteNames: string[];
}

export const EMPTY_TIMESHEET_FILTERS: TimesheetFilters = {
  search: '',
  statuses: [],
  clientNames: [],
  accountManagers: [],
  siteNames: [],
};

const matchesSelection = (
  selected: string[],
  value: string | null | undefined,
): boolean => selected.length === 0 || (!!value && selected.includes(value));

export const filterTimesheetItems = (
  items: TimesheetApprovalItem[],
  filters: TimesheetFilters,
  accountManagerByClientId: ReadonlyMap<string, string> = new Map(),
): TimesheetApprovalItem[] => {
  const search = filters.search.trim().toLowerCase();
  return items.filter((item) => {
    if (
      search &&
      ![item.workerName, item.jobTitle, item.siteName, item.clientName].some(
        (value) => value?.toLowerCase().includes(search),
      )
    ) {
      return false;
    }
    return (
      matchesSelection(filters.statuses, item.daySequence.status) &&
      matchesSelection(filters.clientNames, item.clientName) &&
      matchesSelection(
        filters.accountManagers,
        accountManagerByClientId.get(item.clientId),
      ) &&
      matchesSelection(filters.siteNames, item.siteName)
    );
  });
};

/**
 * Groups timesheets by client, then candidate. A candidate who worked for two
 * clients appears on two rows. Items in each row are sorted by date.
 */
export const groupTimesheetsByClient = (
  items: TimesheetApprovalItem[],
): TimesheetGroup[] => {
  const groupsByRowKey = new Map<string, TimesheetGroup>();

  items.forEach((item) => {
    const userId = item.daySequence.userId;
    if (!userId) {
      return;
    }
    const clientId = item.clientId || '';
    const rowKey = timesheetRowKey(clientId, userId);
    const existing = groupsByRowKey.get(rowKey);
    if (existing) {
      existing.items.push(item);
      return;
    }
    groupsByRowKey.set(rowKey, {
      rowKey,
      clientId,
      clientName: item.clientName?.trim() || 'Unknown client',
      userId,
      workerName: item.workerName?.trim() || 'Unknown',
      workerProfileImageUrl: item.workerProfileImageUrl,
      items: [item],
      startDateISO: item.daySequence.startDateISO,
      endDateISO: item.daySequence.startDateISO,
    });
  });

  return Array.from(groupsByRowKey.values())
    .map((group) => {
      const sortedItems = [...group.items].sort((left, right) =>
        left.daySequence.startDateISO.localeCompare(
          right.daySequence.startDateISO,
        ),
      );
      return {
        ...group,
        items: sortedItems,
        startDateISO: sortedItems[0].daySequence.startDateISO,
        endDateISO:
          sortedItems[sortedItems.length - 1].daySequence.startDateISO,
      };
    })
    .sort((left, right) => {
      const byClient = left.clientName.localeCompare(right.clientName);
      if (byClient !== 0) return byClient;
      return left.workerName.localeCompare(right.workerName);
    });
};

/** e.g. "1 Jun – 7 Jun", "1 Jun" or "28 Dec 2026 – 3 Jan 2027". */
export const formatTimesheetDateRange = (
  startDateISO: string,
  endDateISO: string,
): string => {
  const start = dayjs(startDateISO);
  const end = dayjs(endDateISO);
  if (!start.isValid() || !end.isValid()) {
    return '-';
  }
  if (start.isSame(end, 'day')) {
    return start.format('D MMM');
  }
  if (start.year() !== end.year()) {
    return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
  }
  return `${start.format('D MMM')} – ${end.format('D MMM')}`;
};

export type TimesheetTimeSort = 'asc' | 'desc';

/** One pay cycle of a candidate's timesheets. */
export interface TimesheetPayCycleGroup {
  key: string;
  startDateISO: string;
  endDateISO: string;
  items: TimesheetApprovalItem[];
}

/** Same fallback as the backend core loop configuration default (a Monday). */
export const DEFAULT_PAY_RUN_CYCLE_ANCHOR_DATE = '2026-10-05';

export const resolveTimesheetSubmissionFrequency = (
  frequency: TimesheetSubmissionFrequency | null | undefined,
): TimesheetSubmissionFrequency =>
  frequency ?? TimesheetSubmissionFrequency.Day;

/** Pay-run settings from the core loop configuration, with defaults filled in. */
export interface PayRunSettings {
  frequency: TimesheetSubmissionFrequency | null | undefined;
  anchorDate: string | null | undefined;
}

const toDateISO = (dateISO: string): string => {
  const date = dayjs(dateISO);
  return date.isValid() ? date.format('YYYY-MM-DD') : '';
};

/**
 * The pay period containing `dateISO`, from the same anchor and frequency the
 * worker app uses when candidates submit.
 */
const payCycleWindow = (
  dateISO: string,
  settings: PayRunSettings,
): { key: string; startDateISO: string; endDateISO: string } => {
  const day = toDateISO(dateISO);
  if (!day) {
    return { key: 'unknown', startDateISO: '', endDateISO: '' };
  }
  const period = getCurrentPayrunCycleDate(
    settings.anchorDate || DEFAULT_PAY_RUN_CYCLE_ANCHOR_DATE,
    resolveTimesheetSubmissionFrequency(settings.frequency),
    day,
  );
  return {
    key: period.startDate,
    startDateISO: period.startDate,
    endDateISO: period.endDate,
  };
};

const timesheetSortValue = (item: TimesheetApprovalItem): number => {
  const clockTime =
    item.daySequence.clockInTime ||
    item.daySequence.proposedClockInTime ||
    item.daySequence.startDate;
  const timestamp = Number(clockTime);
  if (!Number.isNaN(timestamp) && timestamp > 0) {
    return timestamp;
  }
  const date = dayjs(item.daySequence.startDateISO);
  return date.isValid() ? date.valueOf() : 0;
};

/**
 * Splits a candidate's timesheets into pay cycles from the core loop
 * frequency, then sorts cycles and timesheets by time.
 */
export const groupTimesheetsByPayCycle = (
  items: TimesheetApprovalItem[],
  settings: PayRunSettings,
  sort: TimesheetTimeSort,
): TimesheetPayCycleGroup[] => {
  const groupsByKey = new Map<string, TimesheetPayCycleGroup>();

  items.forEach((item) => {
    const window = payCycleWindow(item.daySequence.startDateISO, settings);
    const existing = groupsByKey.get(window.key);
    if (existing) {
      existing.items.push(item);
      return;
    }
    groupsByKey.set(window.key, {
      key: window.key,
      startDateISO: window.startDateISO,
      endDateISO: window.endDateISO,
      items: [item],
    });
  });

  const direction = sort === 'asc' ? 1 : -1;
  return Array.from(groupsByKey.values())
    .map((group) => ({
      ...group,
      items: [...group.items].sort(
        (left, right) =>
          (timesheetSortValue(left) - timesheetSortValue(right)) * direction,
      ),
    }))
    .sort(
      (left, right) =>
        left.startDateISO.localeCompare(right.startDateISO) * direction,
    );
};

export type PayPeriodSelection = 'previous' | 'current';

export interface PayPeriod {
  startDateISO: string;
  endDateISO: string;
}

/** The pay period before today's, and today's. */
export const selectablePayPeriods = (
  settings: PayRunSettings,
  today: Dayjs = dayjs(),
): Record<PayPeriodSelection, PayPeriod> => {
  const anchorDate = settings.anchorDate || DEFAULT_PAY_RUN_CYCLE_ANCHOR_DATE;
  const frequency = resolveTimesheetSubmissionFrequency(settings.frequency);
  const todayISO = today.format('YYYY-MM-DD');
  const previous = getLastPayrunCycleDate(anchorDate, frequency, todayISO);
  const current = getCurrentPayrunCycleDate(anchorDate, frequency, todayISO);
  return {
    previous: {
      startDateISO: previous.startDate,
      endDateISO: previous.endDate,
    },
    current: { startDateISO: current.startDate, endDateISO: current.endDate },
  };
};

export const isInPayPeriod = (dateISO: string, period: PayPeriod): boolean => {
  const day = toDateISO(dateISO);
  return !!day && day >= period.startDateISO && day <= period.endDateISO;
};

/** Keeps timesheets whose shift starts in the selected pay period. */
export const filterTimesheetsToPayPeriod = (
  items: TimesheetApprovalItem[],
  period: PayPeriod,
): TimesheetApprovalItem[] =>
  items.filter((item) => isInPayPeriod(item.daySequence.startDateISO, period));

const calendarDateISO = (
  unixMs?: string | null,
  fallback?: string | null,
): string => {
  if (unixMs) {
    const timestamp = Number(unixMs);
    if (!Number.isNaN(timestamp)) {
      return dayjs(timestamp).format('YYYY-MM-DD');
    }
  }
  if (fallback && dayjs(fallback).isValid()) {
    return dayjs(fallback).format('YYYY-MM-DD');
  }
  return dayjs().format('YYYY-MM-DD');
};

export const buildTimesheetChange = (
  item: TimesheetApprovalItem,
  startTime: string,
  endTime: string,
  breakMinutes: number,
): DaySequenceChangeInput | null => {
  const { daySequence } = item;
  const clockInMs =
    daySequence.clockInTime || daySequence.proposedClockInTime || null;
  const startDate = calendarDateISO(clockInMs, daySequence.startDateISO);

  if (!startTime || !endTime) {
    return null;
  }

  const clockInTimestamp = getTimestampFromTime(startDate, startTime);
  let endDate = startDate;
  let clockOutTimestamp = getTimestampFromTime(endDate, endTime);
  if (Number(clockOutTimestamp) <= Number(clockInTimestamp)) {
    endDate = dayjs(startDate).add(1, 'day').format('YYYY-MM-DD');
    clockOutTimestamp = getTimestampFromTime(endDate, endTime);
  }
  if (
    !clockInTimestamp ||
    !clockOutTimestamp ||
    Number(clockOutTimestamp) <= Number(clockInTimestamp)
  ) {
    return null;
  }

  return {
    id: daySequence.id,
    clockInTimestamp,
    clockOutTimestamp,
    proposedBreaks:
      breakMinutes > 0
        ? [
            {
              daySequenceId: daySequence.id,
              duration: String(minutesToMilliseconds(breakMinutes)),
              type: BreakType.Unpaid,
            },
          ]
        : [],
  };
};
